import {
  REQUEST_ID_HEADER,
  mintRequestId,
  tagRequestId,
} from "@sill/shared/observability/requestId";
import { captureHandledError } from "@sill/shared/observability/captureHandledError";

export const API_BASE = import.meta.env.VITE_API_URL || "";

export type RequestOptions = {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  allowReverificationOnSecondFactorSetupRequired?: boolean;
  reverification?: ReverificationConfig;
  token?: string;
  signal?: AbortSignal;
  /**
   * Safe diagnostic label for a request whose concrete path contains a
   * credential-like value (for example a marketplace claim token). The real
   * path is still sent to the API; this label is used only in client-side
   * error/MFA/paywall instrumentation.
   */
  observabilityPath?: string;
};

export const MFA_REQUIRED_EVENT = "sill:mfa-required";

export type ReverificationConfig = {
  afterMinutes: number;
  level: "first_factor" | "second_factor" | "multi_factor";
};

export type ClerkReverificationHint = {
  clerk_error: {
    metadata: {
      reverification: ReverificationConfig;
    };
    reason: "reverification-error";
    type: "forbidden";
  };
  requires_second_factor_setup?: boolean;
};

// ApiErrorPayload is the Clerk-shaped MFA / reverification hint surfaced
// on `ApiError.payload`. Keeps the MFA flow intact (main / SILL-286).
type ApiErrorPayload = {
  clerk_error?: ClerkReverificationHint["clerk_error"];
  code?: string;
  factor_age_max_seconds?: number;
  requires_second_factor_setup?: boolean;
  // SILL-302 toast fix (2abcf14): backend errors return
  // `{"error": "<msg>", "request_id": "..."}`. Surface `.error` as the
  // user-facing string so toasts don't render raw JSON.
  error?: string;
  request_id?: string;
};

// ApiErrorBody is the wider view of the same JSON error body, exposed on
// `ApiError.structured` so the Sill Plus http-paywall-interceptor (and
// other code-based dispatchers) can introspect `code`/`feature`/`scope`
// without overlapping with the MFA payload shape. Populated alongside
// `payload` in handleApiFailure / buildApiError.
export interface ApiErrorBody {
  code?: string;
  feature?: string;
  scope?: string;
  held_by?: string;
  message?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  public code?: string;
  public payload?: ApiErrorPayload;
  // structured carries the same JSON body as `payload`, but typed for
  // generic-code dispatch (paywall interceptor, future feature gates).
  // It overlaps with `payload` by design — both reference the same
  // parsed object so consumers can pick the view that fits.
  public structured?: ApiErrorBody;

  constructor(
    public status: number,
    message: string,
    payload?: ApiErrorPayload,
    structured?: ApiErrorBody,
  ) {
    super(message);
    this.name = "ApiError";
    this.payload = payload;
    this.code = payload?.code ?? structured?.code;
    this.structured = structured;
  }
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const {
    method = "GET",
    body,
    headers = {},
    allowReverificationOnSecondFactorSetupRequired = false,
    reverification,
    token,
    signal,
    observabilityPath,
  } = options;
  const isFormData = body instanceof FormData;

  const allHeaders: Record<string, string> = isFormData
    ? { ...headers }
    : {
        "Content-Type": "application/json",
        ...headers,
      };

  if (token) {
    allHeaders["Authorization"] = `Bearer ${token}`;
  }

  // Mint per-request id and tag the active Sentry scope so a backend 5xx,
  // its log line, and the FE-side Sentry event all share the same request_id.
  const requestId = mintRequestId();
  tagRequestId(requestId);
  allHeaders[REQUEST_ID_HEADER] = requestId;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: allHeaders,
    body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
    credentials: "include",
    signal,
  });

  if (!res.ok) {
    return handleApiFailure<T>(res, observabilityPath ?? path, {
      allowReverificationOnSecondFactorSetupRequired,
      reverification,
      requestId,
    });
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return res.json();
}

export async function apiBlobRequest(
  path: string,
  options: RequestOptions = {},
): Promise<{ blob: Blob; filename?: string }> {
  const { method = "GET", body, headers = {}, token, signal } = options;
  const isFormData = body instanceof FormData;
  const allHeaders: Record<string, string> = isFormData
    ? { ...headers }
    : {
        "Content-Type": "application/json",
        ...headers,
      };

  if (token) {
    allHeaders["Authorization"] = `Bearer ${token}`;
  }

  const requestId = mintRequestId();
  tagRequestId(requestId);
  allHeaders[REQUEST_ID_HEADER] = requestId;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: allHeaders,
    body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
    credentials: "include",
    signal,
  });

  if (!res.ok) {
    const err = await buildApiError(res, path);
    // mfa_required is an expected step-up prompt, not a defect (see apiRequest).
    if (err.payload?.code !== "mfa_required") {
      captureHandledError(`apiBlobRequest:${path}`, err, { requestId });
    }
    throw err;
  }

  return {
    blob: await res.blob(),
    filename: filenameFromContentDisposition(
      res.headers.get("Content-Disposition"),
    ),
  };
}

async function buildApiError(res: Response, path: string): Promise<ApiError> {
  const text = await res.text().catch(() => "Unknown error");
  const payload = parseApiErrorPayload(text);
  const structured = parseApiErrorBody(text);
  dispatchMFARequired(payload, path);
  const err = new ApiError(
    res.status,
    apiErrorMessage(text, payload),
    payload,
    structured,
  );
  notifyPaywallInterceptor(err, path);
  return err;
}

// notifyPaywallInterceptor fires the Sill Plus paywall event bus when the
// error matches `code:"feature_not_available"`. Lazy-imported to avoid a
// circular module dep (the interceptor imports ApiError from this file).
// Fire-and-forget — paywall side-effects must never block or alter the
// thrown error. Mirror the same call into handleApiFailure so the MFA
// path also triggers paywalls for 403s that aren't MFA-related.
function notifyPaywallInterceptor(err: ApiError, path: string): void {
  if (err.status !== 403 || err.structured?.code !== "feature_not_available") {
    return;
  }
  void import("../components/billing/http-paywall-interceptor")
    .then((mod) => mod.inspectErrorForPaywall(err, path))
    .catch(() => {});
}

async function handleApiFailure<T>(
  res: Response,
  path: string,
  options: {
    allowReverificationOnSecondFactorSetupRequired?: boolean;
    reverification?: ReverificationConfig;
    requestId?: string;
  } = {},
): Promise<T> {
  const text = await res.text().catch(() => "Unknown error");
  const payload = parseApiErrorPayload(text);
  // parseApiErrorBody returns the same underlying JSON typed for generic
  // dispatch (paywall interceptor needs structured.code / .feature without
  // poking through the Clerk-shaped ApiErrorPayload).
  const structured = parseApiErrorBody(text);
  if (
    payload?.code === "mfa_required" &&
    payload.requires_second_factor_setup &&
    !options.allowReverificationOnSecondFactorSetupRequired
  ) {
    // Setup-required is not a stale-session MFA check. Let the caller render
    // its first-class enrollment path instead of opening the global fallback.
    const err = new ApiError(
      res.status,
      apiErrorMessage(text, payload),
      payload,
      structured,
    );
    notifyPaywallInterceptor(err, path);
    // mfa_required is an expected step-up/enrollment prompt, not a defect.
    throw err;
  }
  if (payload?.code === "mfa_required" && options.reverification) {
    return createClerkReverificationHint(
      payload.clerk_error?.metadata?.reverification ?? options.reverification,
    ) as T;
  }
  dispatchMFARequired(payload, path);
  const err = new ApiError(
    res.status,
    apiErrorMessage(text, payload),
    payload,
    structured,
  );
  notifyPaywallInterceptor(err, path);
  // mfa_required / reverification_required are expected step-up prompts
  // (the MFA dialog / a useReverification caller handles them); reporting
  // them to Sentry is noise. Every other failure is still captured.
  if (payload?.code !== "mfa_required" && payload?.code !== "reverification_required") {
    captureHandledError(`apiRequest:${path}`, err, { requestId: options.requestId });
  }
  throw err;
}

// apiErrorMessage returns the cleanest user-facing string available:
// backend errors land as `{"error":"<msg>","request_id":"..."}`, so we
// surface `.error` when present. Falls back to raw text for plaintext
// bodies. Toast/error UI is responsible for the 5xx generic fallback
// (see documents-view review handler) so we don't second-guess copy here.
function apiErrorMessage(text: string, payload: ApiErrorPayload | undefined): string {
  if (payload?.error && payload.error.trim() !== "") {
    return payload.error;
  }
  return text;
}

function dispatchMFARequired(
  payload: ApiErrorPayload | undefined,
  path: string,
) {
  if (payload?.code === "mfa_required" && typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(MFA_REQUIRED_EVENT, {
        detail: {
          factorAgeMaxSeconds: payload.factor_age_max_seconds,
          path,
        },
      }),
    );
  }
}

export function isClerkReverificationHint(
  value: unknown,
): value is ClerkReverificationHint {
  return (
    typeof value === "object" &&
    value !== null &&
    "clerk_error" in value &&
    (value as ClerkReverificationHint).clerk_error?.type === "forbidden" &&
    (value as ClerkReverificationHint).clerk_error?.reason ===
      "reverification-error"
  );
}

function createClerkReverificationHint(
  reverification: ReverificationConfig,
): ClerkReverificationHint {
  return {
    clerk_error: {
      metadata: { reverification },
      reason: "reverification-error",
      type: "forbidden",
    },
  };
}

function parseApiErrorPayload(text: string): ApiErrorPayload | undefined {
  try {
    const parsed = JSON.parse(text) as ApiErrorPayload;
    if (parsed && typeof parsed === "object") {
      return parsed;
    }
  } catch {
    // Keep ApiError.message as the raw body for existing error-copy helpers.
  }
  return undefined;
}

// parseApiErrorBody is the structured-view parser for the same JSON the
// MFA flow already reads via parseApiErrorPayload. Returns undefined for
// non-JSON bodies so existing rendering keeps the raw text as `message`.
function parseApiErrorBody(text: string): ApiErrorBody | undefined {
  try {
    const parsed = JSON.parse(text) as ApiErrorBody;
    if (parsed && typeof parsed === "object") {
      return parsed;
    }
  } catch {
    // Not JSON — paywall interceptor will not match without `code`.
  }
  return undefined;
}

function filenameFromContentDisposition(value: string | null) {
  if (!value) {
    return undefined;
  }
  const match = /filename="?([^";]+)"?/i.exec(value);
  return match?.[1];
}

export const api = {
  get: <T>(path: string, token?: string) => apiRequest<T>(path, { token }),
  post: <T>(path: string, body: unknown, token?: string) =>
    apiRequest<T>(path, { method: "POST", body, token }),
  put: <T>(path: string, body: unknown, token?: string) =>
    apiRequest<T>(path, { method: "PUT", body, token }),
  patch: <T>(path: string, body: unknown, token?: string) =>
    apiRequest<T>(path, { method: "PATCH", body, token }),
  delete: <T>(path: string, token?: string, body?: unknown) =>
    apiRequest<T>(path, { method: "DELETE", token, body }),
};

// readinessApi wraps the v2 readiness HTTP surface registered in
// `backend/internal/api/readiness.go`. Routes are `/api/readiness*` (echo v5
// group), not `/api/v1/readiness*`. Token is the Clerk session JWT — pass
// `undefined` only in tests that mock fetch.
import type {
  PutReadinessTargetRequest,
  ReadinessActiveResponse,
  ReadinessHistoryMode,
  ReadinessHistoryResponse,
  ReadinessPreviewResponse,
  ReadinessTarget,
} from "./readiness-types";

export const readinessApi = {
  getActive: (token?: string) =>
    api.get<ReadinessActiveResponse>("/api/readiness", token),
  setTarget: (body: PutReadinessTargetRequest, token?: string) =>
    api.put<ReadinessActiveResponse>("/api/readiness/target", body, token),
  preview: (target: ReadinessTarget, token?: string) =>
    api.post<ReadinessPreviewResponse>("/api/readiness/preview", target, token),
  recalculate: (token?: string) =>
    api.post<
      ReadinessActiveResponse & { recalculated: boolean; reason: string }
    >("/api/readiness/recalculate", {}, token),
  history: (
    opts: { since?: string; mode?: ReadinessHistoryMode } = {},
    token?: string,
  ) => {
    const params = new URLSearchParams();
    if (opts.since) params.set("since", opts.since);
    if (opts.mode) params.set("mode", opts.mode);
    params.set("window", "90d");
    return api.get<ReadinessHistoryResponse>(
      `/api/readiness/history?${params.toString()}`,
      token,
    );
  },
};
